import { Header } from "@/components/navigation/Header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Brain, Cpu, Zap, Users } from "lucide-react";
import { useState } from "react";

const Models = () => {
  const [selectedUseCase, setSelectedUseCase] = useState<string>("all");
  const [selectedSize, setSelectedSize] = useState<string>("all");

  const models = [
    {
      name: "Llama 2 70B",
      description: "Large language model optimized for conversational AI",
      size: "70B",
      useCase: "conversation",
      performance: "High",
      memory: "140GB",
      throughput: "45 tokens/sec",
      recommended: true
    },
    {
      name: "Code Llama 34B", 
      description: "Specialized model for code generation and analysis",
      size: "34B",
      useCase: "coding",
      performance: "High",
      memory: "68GB", 
      throughput: "78 tokens/sec",
      recommended: false
    },
    {
      name: "Mistral 7B Instruct",
      description: "Efficient instruction-following model",
      size: "7B",
      useCase: "general",
      performance: "Medium",
      memory: "14GB",
      throughput: "120 tokens/sec",
      recommended: true
    },
    {
      name: "Falcon 180B",
      description: "Large general-purpose language model",
      size: "180B", 
      useCase: "general",
      performance: "Very High",
      memory: "360GB",
      throughput: "28 tokens/sec",
      recommended: false
    }
  ];

  const useCases = [
    { value: "all", label: "All Use Cases" },
    { value: "conversation", label: "Conversational AI" },
    { value: "coding", label: "Code Generation" },
    { value: "general", label: "General Purpose" }
  ];

  const sizes = [
    { value: "all", label: "All Sizes" },
    { value: "7B", label: "7B Parameters" },
    { value: "34B", label: "34B Parameters" },
    { value: "70B", label: "70B Parameters" },
    { value: "180B", label: "180B Parameters" }
  ];

  const filteredModels = models.filter(model => {
    const useCaseMatch = selectedUseCase === "all" || model.useCase === selectedUseCase;
    const sizeMatch = selectedSize === "all" || model.size === selectedSize;
    return useCaseMatch && sizeMatch;
  });

  const getPerformanceColor = (performance: string) => {
    switch (performance) {
      case "Very High": return "text-purple-600 dark:text-purple-400";
      case "High": return "text-blue-600 dark:text-blue-400";
      case "Medium": return "text-green-600 dark:text-green-400";
      default: return "text-muted-foreground";
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-8">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl font-bold mb-2">Model Selector</h1>
              <p className="text-muted-foreground text-lg">
                Choose the optimal AI model for your use case with guided recommendations
              </p>
            </div>

            <Tabs defaultValue="browse" className="space-y-6">
              <TabsList className="grid w-full grid-cols-2 lg:w-400">
                <TabsTrigger value="browse">Browse Models</TabsTrigger>
                <TabsTrigger value="wizard">Guided Wizard</TabsTrigger>
              </TabsList>

              <TabsContent value="browse" className="space-y-6">
                {/* Filters */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Filter Models</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium mb-2 block">Use Case</label>
                        <Select value={selectedUseCase} onValueChange={setSelectedUseCase}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {useCases.map((useCase) => (
                              <SelectItem key={useCase.value} value={useCase.value}>
                                {useCase.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">Model Size</label>
                        <Select value={selectedSize} onValueChange={setSelectedSize}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {sizes.map((size) => (
                              <SelectItem key={size.value} value={size.value}>
                                {size.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Models Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {filteredModels.map((model) => (
                    <Card key={model.name} className={`hover:shadow-glow transition-smooth ${model.recommended ? 'ring-2 ring-primary/20' : ''}`}>
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                              <Brain className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                              <CardTitle className="text-lg">{model.name}</CardTitle>
                              {model.recommended && (
                                <Badge className="mt-1 bg-primary/20 text-primary">
                                  Recommended
                                </Badge>
                              )}
                            </div>
                          </div>
                          <Badge variant="outline">{model.size}</Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <CardDescription className="mb-4">
                          {model.description}
                        </CardDescription>
                        
                        <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                          <div className="flex items-center space-x-2">
                            <Cpu className="h-4 w-4 text-muted-foreground" />
                            <span>Memory: {model.memory}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Zap className="h-4 w-4 text-muted-foreground" />
                            <span>Speed: {model.throughput}</span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="text-sm">
                            Performance: <span className={`font-medium ${getPerformanceColor(model.performance)}`}>
                              {model.performance}
                            </span>
                          </div>
                          <Button size="sm">
                            Select Model
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="wizard" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Model Selection Wizard</CardTitle>
                    <CardDescription>
                      Answer a few questions to get personalized model recommendations
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      <div className="text-center py-12">
                        <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                        <h3 className="text-lg font-medium mb-2">Guided Model Selection</h3>
                        <p className="text-muted-foreground mb-6">
                          Coming soon - Get AI-powered recommendations based on your specific requirements
                        </p>
                        <Button variant="outline" disabled>
                          Start Wizard
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Models;